import type {
  AuthClientMetadata,
  AuthEnvironmentScope,
  AuthPairingCredentialResult,
  ServerAuthSessionMethod,
  AuthSessionId,
  AuthSessionState,
} from "@amoreg/contracts";
import {
  EnvironmentAuthInvalidError,
  EnvironmentHttpCommonError,
  PRIMARY_LOCAL_ENVIRONMENT_ID,
} from "@amoreg/contracts";
import * as DateTime from "effect/DateTime";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import * as Schedule from "effect/Schedule";
import {
  bootstrapCredentialExchange,
  recoverBootstrapRead,
  withBootstrapDeadline,
  PrimaryEnvironmentRequestError,
} from "./bootstrapRequest";
export {
  PrimaryEnvironmentRequestError,
  isPrimaryEnvironmentRequestError,
} from "./bootstrapRequest";

import {
  getPairingTokenFromUrlFragment,
  stripPairingTokenFromUrl as stripPairingTokenUrl,
} from "../../pairingUrl";

import { PrimaryEnvironmentHttpClient } from "./httpClient";
import { runPrimaryHttp } from "../../lib/runtime";

export class PrimaryEnvironmentPairingCredentialRejectedError extends Schema.TaggedErrorClass<PrimaryEnvironmentPairingCredentialRejectedError>()(
  "PrimaryEnvironmentPairingCredentialRejectedError",
  {
    providedLength: Schema.Number,
    cause: EnvironmentAuthInvalidError,
  },
) {
  override get message(): string {
    return "Invalid pairing token. Check the token and try again.";
  }
}

export const isPrimaryEnvironmentPairingCredentialRejectedError = Schema.is(
  PrimaryEnvironmentPairingCredentialRejectedError,
);

export class PrimaryEnvironmentAuthSessionTimeoutError extends Schema.TaggedErrorClass<PrimaryEnvironmentAuthSessionTimeoutError>()(
  "PrimaryEnvironmentAuthSessionTimeoutError",
  {
    timeoutMs: Schema.Number,
    elapsedMs: Schema.Number,
  },
) {
  override get message(): string {
    return "Timed out waiting for authenticated session after bootstrap.";
  }
}

export const isPrimaryEnvironmentAuthSessionTimeoutError = Schema.is(
  PrimaryEnvironmentAuthSessionTimeoutError,
);

export class PrimaryEnvironmentPairingCredentialRequiredError extends Schema.TaggedErrorClass<PrimaryEnvironmentPairingCredentialRequiredError>()(
  "PrimaryEnvironmentPairingCredentialRequiredError",
  {
    providedLength: Schema.Number,
  },
) {
  override get message(): string {
    return "Enter a pairing token to continue.";
  }
}

export const isPrimaryEnvironmentPairingCredentialRequiredError = Schema.is(
  PrimaryEnvironmentPairingCredentialRequiredError,
);

const isEnvironmentHttpCommonError = Schema.is(EnvironmentHttpCommonError);

export interface ServerPairingLinkRecord {
  readonly id: string;
  readonly credential: string;
  readonly scopes: ReadonlyArray<AuthEnvironmentScope>;
  readonly subject: string;
  readonly label?: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface ServerClientSessionRecord {
  readonly sessionId: AuthSessionId;
  readonly subject: string;
  readonly scopes: ReadonlyArray<AuthEnvironmentScope>;
  readonly method: ServerAuthSessionMethod;
  readonly client: AuthClientMetadata;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly lastConnectedAt: string | null;
  readonly connected: boolean;
  readonly current: boolean;
}

type ServerAuthGateState =
  | { status: "authenticated" }
  | {
      status: "requires-auth";
      auth: AuthSessionState["auth"];
      errorMessage?: string;
    };

let bootstrapPromise: Promise<ServerAuthGateState> | null = null;
let resolvedAuthenticatedGateState: ServerAuthGateState | null = null;
const AUTH_SESSION_ESTABLISH_TIMEOUT_MS = 2_000;
const AUTH_SESSION_ESTABLISH_STEP_MS = 100;

/**
 * Reads the one-time pairing credential from the current location's fragment.
 * Fragment only: a `?token=…` link is never honoured, so a leaked or crafted
 * query-string credential cannot be exchanged for a session here.
 */
export function peekPairingTokenFromUrl(): string | null {
  return getPairingTokenFromUrlFragment(new URL(window.location.href));
}

export function stripPairingTokenFromUrl() {
  const url = new URL(window.location.href);
  const next = stripPairingTokenUrl(url);
  if (next.toString() === url.toString()) {
    return;
  }
  window.history.replaceState({}, document.title, next.toString());
}

export function takePairingTokenFromUrl(): string | null {
  const token = peekPairingTokenFromUrl();
  // Always scrub, even when nothing is consumed: a leaked or crafted
  // `?token=…` is never honoured, but it must not linger in the address bar,
  // history or Referer either. The shared strip removes both forms and is a
  // no-op when there is nothing to remove.
  stripPairingTokenFromUrl();
  return token;
}

function getStaticBootstrapCredential(): string | null {
  // Baked in at build time for hosted single-server deployments (paired with
  // the server's AMOREG_STATIC_BOOTSTRAP_TOKEN). Presence of the token IS the
  // deployment's auto-pair switch: the exchange happens before any route
  // renders, so the pairing UI never appears.
  const token = import.meta.env.VITE_T3_BOOTSTRAP_TOKEN;
  return typeof token === "string" && token.length > 0 ? token : null;
}

function getDesktopBootstrapCredential(): string | null {
  // Both backends share the same bootstrap token (DesktopBackendConfiguration
  // mints one tokenRef and feeds it to both resolvers), so picking the
  // primary entry is fine even when the WSL backend is also registered.
  const bootstraps = window.desktopBridge?.getLocalEnvironmentBootstraps() ?? [];
  const primary = bootstraps.find((entry) => entry.id === PRIMARY_LOCAL_ENVIRONMENT_ID);
  return typeof primary?.bootstrapToken === "string" && primary.bootstrapToken.length > 0
    ? primary.bootstrapToken
    : null;
}

const readSessionState = PrimaryEnvironmentHttpClient.pipe(
  Effect.flatMap((client) => client.auth.session({ headers: {} })),
  recoverBootstrapRead,
  Effect.mapError((cause) =>
    PrimaryEnvironmentRequestError.fromCause({ operation: "fetch-session-state", cause }),
  ),
);

export function fetchSessionState(): Promise<AuthSessionState> {
  return runPrimaryHttp(readSessionState);
}

function exchangeBootstrapCredential(credential: string) {
  return PrimaryEnvironmentHttpClient.pipe(
    Effect.flatMap((client) => client.auth.browserSession({ payload: { credential } })),
    bootstrapCredentialExchange,
    Effect.mapError((cause) => {
      if (
        isEnvironmentHttpCommonError(cause) &&
        cause._tag === "EnvironmentAuthInvalidError" &&
        cause.reason === "invalid_credential"
      ) {
        return new PrimaryEnvironmentPairingCredentialRejectedError({
          providedLength: credential.length,
          cause,
        });
      }
      return PrimaryEnvironmentRequestError.fromCause({
        operation: "exchange-bootstrap-credential",
        cause,
      });
    }),
  );
}

const waitForAuthenticatedSessionAfterBootstrap = readSessionState.pipe(
  Effect.repeat({
    while: (session) => !session.authenticated,
    schedule: Schedule.spaced(AUTH_SESSION_ESTABLISH_STEP_MS),
  }),
  Effect.timeoutOrElse({
    duration: AUTH_SESSION_ESTABLISH_TIMEOUT_MS,
    orElse: () =>
      Effect.fail(
        new PrimaryEnvironmentAuthSessionTimeoutError({
          timeoutMs: AUTH_SESSION_ESTABLISH_TIMEOUT_MS,
          elapsedMs: AUTH_SESSION_ESTABLISH_TIMEOUT_MS,
        }),
      ),
  }),
);

function bootstrapServerAuth(): Promise<ServerAuthGateState> {
  return runPrimaryHttp(
    Effect.gen(function* () {
      const bootstrapCredential = getDesktopBootstrapCredential() ?? getStaticBootstrapCredential();
      const currentSession = yield* readSessionState;
      if (currentSession.authenticated) return { status: "authenticated" } as const;
      if (!bootstrapCredential)
        return { status: "requires-auth", auth: currentSession.auth } as const;

      return yield* exchangeBootstrapCredential(bootstrapCredential).pipe(
        Effect.andThen(waitForAuthenticatedSessionAfterBootstrap),
        Effect.as({ status: "authenticated" } as const),
        Effect.catch((error) =>
          Effect.succeed({
            status: "requires-auth" as const,
            auth: currentSession.auth,
            errorMessage: error.message,
          }),
        ),
      );
    }).pipe(
      withBootstrapDeadline,
      Effect.mapError((cause) =>
        cause instanceof PrimaryEnvironmentRequestError
          ? cause
          : PrimaryEnvironmentRequestError.fromCause({ operation: "bootstrap-authentication", cause }),
      ),
    ),
  );
}

export async function submitServerAuthCredential(credential: string): Promise<void> {
  const trimmedCredential = credential.trim();
  if (!trimmedCredential) {
    throw new PrimaryEnvironmentPairingCredentialRequiredError({
      providedLength: credential.length,
    });
  }

  resolvedAuthenticatedGateState = null;
  await runPrimaryHttp(exchangeBootstrapCredential(trimmedCredential));
  bootstrapPromise = null;
  stripPairingTokenFromUrl();
}

export async function createServerPairingCredential(input?: {
  readonly label?: string;
  readonly scopes?: ReadonlyArray<AuthEnvironmentScope>;
}): Promise<AuthPairingCredentialResult> {
  const trimmedLabel = input?.label?.trim();
  try {
    return await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) =>
          client.auth.pairingCredential({
            headers: {},
            payload: {
              ...(trimmedLabel ? { label: trimmedLabel } : {}),
              ...(input?.scopes ? { scopes: input.scopes } : {}),
            },
          }),
        ),
      ),
    );
  } catch (error) {
    throw PrimaryEnvironmentRequestError.fromCause({
      operation: "create-pairing-credential",
      cause: error,
    });
  }
}

export async function listServerPairingLinks(): Promise<ReadonlyArray<ServerPairingLinkRecord>> {
  try {
    const pairingLinks = await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.pairingLinks({ headers: {} })),
      ),
    );
    return pairingLinks.map((pairingLink) => {
      const timestamps = {
        createdAt: DateTime.formatIso(pairingLink.createdAt),
        expiresAt: DateTime.formatIso(pairingLink.expiresAt),
      };
      if (pairingLink.label === undefined) {
        return {
          id: pairingLink.id,
          credential: pairingLink.credential,
          scopes: pairingLink.scopes,
          subject: pairingLink.subject,
          createdAt: timestamps.createdAt,
          expiresAt: timestamps.expiresAt,
        };
      }
      return {
        id: pairingLink.id,
        credential: pairingLink.credential,
        scopes: pairingLink.scopes,
        subject: pairingLink.subject,
        label: pairingLink.label,
        createdAt: timestamps.createdAt,
        expiresAt: timestamps.expiresAt,
      };
    });
  } catch (error) {
    throw PrimaryEnvironmentRequestError.fromCause({
      operation: "list-pairing-links",
      cause: error,
    });
  }
}

export async function revokeServerPairingLink(id: string): Promise<void> {
  try {
    await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.revokePairingLink({ headers: {}, payload: { id } })),
      ),
    );
  } catch (error) {
    throw PrimaryEnvironmentRequestError.fromCause({
      operation: "revoke-pairing-link",
      pairingLinkId: id,
      cause: error,
    });
  }
}

export async function listServerClientSessions(): Promise<
  ReadonlyArray<ServerClientSessionRecord>
> {
  try {
    const clientSessions = await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.clients({ headers: {} })),
      ),
    );
    return clientSessions.map((clientSession) => ({
      sessionId: clientSession.sessionId,
      subject: clientSession.subject,
      scopes: clientSession.scopes,
      method: clientSession.method,
      client: clientSession.client,
      issuedAt: DateTime.formatIso(clientSession.issuedAt),
      expiresAt: DateTime.formatIso(clientSession.expiresAt),
      lastConnectedAt:
        clientSession.lastConnectedAt === null
          ? null
          : DateTime.formatIso(clientSession.lastConnectedAt),
      connected: clientSession.connected,
      current: clientSession.current,
    }));
  } catch (error) {
    throw PrimaryEnvironmentRequestError.fromCause({
      operation: "list-client-sessions",
      cause: error,
    });
  }
}

export async function revokeServerClientSession(sessionId: AuthSessionId): Promise<void> {
  try {
    await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) =>
          client.auth.revokeClient({ headers: {}, payload: { sessionId } }),
        ),
      ),
    );
  } catch (error) {
    throw PrimaryEnvironmentRequestError.fromCause({
      operation: "revoke-client-session",
      sessionId,
      cause: error,
    });
  }
}

export async function revokeOtherServerClientSessions(): Promise<number> {
  try {
    const result = await runPrimaryHttp(
      PrimaryEnvironmentHttpClient.pipe(
        Effect.flatMap((client) => client.auth.revokeOtherClients({ headers: {} })),
      ),
    );
    return result.revokedCount;
  } catch (error) {
    throw PrimaryEnvironmentRequestError.fromCause({
      operation: "revoke-other-client-sessions",
      cause: error,
    });
  }
}

export async function resolveInitialServerAuthGateState(): Promise<ServerAuthGateState> {
  if (resolvedAuthenticatedGateState?.status === "authenticated") {
    return resolvedAuthenticatedGateState;
  }

  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  const nextPromise = bootstrapServerAuth();
  bootstrapPromise = nextPromise;
  return nextPromise
    .then((result) => {
      if (result.status === "authenticated") {
        resolvedAuthenticatedGateState = result;
      }
      return result;
    })
    .finally(() => {
      if (bootstrapPromise === nextPromise) {
        bootstrapPromise = null;
      }
    });
}

// Used by the WSL backend swap: invalidate the cached authenticated state
// (the new backend signs sessions with a different key) and re-bootstrap
// against the desktop bootstrap credential so the next WS reconnect doesn't
// hit 401 and start a reauth loop in the renderer.
export async function reauthenticatePrimaryEnvironment(): Promise<ServerAuthGateState> {
  resolvedAuthenticatedGateState = null;
  bootstrapPromise = null;
  return resolveInitialServerAuthGateState();
}

export function __resetServerAuthBootstrapForTests() {
  bootstrapPromise = null;
  resolvedAuthenticatedGateState = null;
}
